/**
 * authDebugLog — persistent auth debug logger
 *
 * Writes auth events to localStorage so they survive redirects and logouts.
 * Keeps the last 300 entries (oldest dropped first).
 *
 * Usage (console):
 *   window.authLog.dump()        → print formatted log to console
 *   window.authLog.copy()        → copy JSON to clipboard
 *   window.authLog.clear()       → wipe stored log
 *   window.authLog.tail(n)       → last n entries
 */

const STORAGE_KEY = 'dry_auth_debug_log';
const MAX_ENTRIES = 300;

function readLog() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function writeLog(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // localStorage full — drop oldest half and retry
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-Math.floor(MAX_ENTRIES / 2))));
    } catch { /* give up */ }
  }
}

export function authLog(event, data = {}) {
  const entry = {
    t: new Date().toISOString(),
    ms: Date.now(),
    event,
    ...data,
  };

  const entries = readLog();
  entries.push(entry);
  if (entries.length > MAX_ENTRIES) {
    entries.splice(0, entries.length - MAX_ENTRIES);
  }
  writeLog(entries);
}

// Log a session start marker on load so we can correlate entries across page loads
if (typeof window !== 'undefined') {
  const entries = readLog();
  entries.push({
    t: new Date().toISOString(),
    ms: Date.now(),
    event: 'SESSION_START',
    url: window.location.href,
    referrer: document.referrer || '',
    ua: navigator.userAgent,
  });
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
  writeLog(entries);
}

// Expose helpers on window for console use
if (typeof window !== 'undefined') {
  window.authLog = {
    dump: () => {
      const entries = readLog();
      console.group(`[authDebugLog] ${entries.length} entries`);
      entries.forEach(e => {
        const { t, ms, event, ...rest } = e;
        const extra = Object.keys(rest).length ? JSON.stringify(rest) : '';
        console.log(`${t}  ${event}  ${extra}`);
      });
      console.groupEnd();
      return entries;
    },
    tail: (n = 20) => {
      const entries = readLog().slice(-n);
      entries.forEach(e => {
        const { t, ms, event, ...rest } = e;
        const extra = Object.keys(rest).length ? JSON.stringify(rest) : '';
        console.log(`${t}  ${event}  ${extra}`);
      });
      return entries;
    },
    copy: async () => {
      const json = JSON.stringify(readLog(), null, 2);
      await navigator.clipboard.writeText(json);
      console.log('[authDebugLog] copied to clipboard');
    },
    clear: () => {
      localStorage.removeItem(STORAGE_KEY);
      console.log('[authDebugLog] cleared');
    },
    raw: () => readLog(),
  };
}

export default authLog;
